/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Inventor Automation team
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////

import React, { Component } from 'react';
import { connect } from 'react-redux';
import './parametersContainer.css';
import Parameter from './parameter';
import { getActiveProject, getParameters, getUpdateParameters, modalProgressShowing, updateFailedShowing, errorData } from '../reducers/mainReducer';
import { fetchParameters, resetParameters, updateModelWithParameters } from '../actions/parametersActions';
import { showModalProgress, showUpdateFailed, invalidateDrawing } from '../actions/uiFlagsActions';
import Button from '@hig/button';
import Tooltip from '@hig/tooltip';
import { Alert24 } from "@hig/icons";

import ModalProgress from './modalProgress';
import ModalFail from './modalFail';
import RfqModal from './RfqModal';
import RegisterPromptModal from './RegisterPromptModal';
import { fullWarningMsg } from '../utils/conversion';
import quoteCartService from '../services/quoteCartService';

export class ParametersContainer extends Component {

    constructor(props) {
        super(props);
        this._isMounted = false;
        this.state = {
            showRfqModal: false,
            addingToQuote: false,
            addToQuoteSuccess: false,
            addToQuoteError: '',
            showRegisterPrompt: false
        };
        this.openRfqModal = this.openRfqModal.bind(this);
        this.closeRfqModal = this.closeRfqModal.bind(this);
        this.handleAddToQuote = this.handleAddToQuote.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
        this.props.fetchParameters(this.props.activeProject.id);
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        if (this.props.activeProject.id !== prevProps.activeProject.id)
            this.props.fetchParameters(this.props.activeProject.id);
    }

    updateClicked() {
        this.props.updateModelWithParameters(this.props.activeProject.id, this.props.projectUpdateParameters);
        this.props.invalidateDrawing();
    }

    onUpdateFailedCloseClick() {
        this.props.showUpdateFailed(false);
    }

    onModalProgressClose() {
        this.props.hideModalProgress();
        if (this.props.onUpdateComplete) {
            // Wait a tick for Redux state to update before advancing
            setTimeout(() => this.props.onUpdateComplete(), 0);
        }
    }

    openRfqModal() {
        this.setState({ showRfqModal: true });
    }

    closeRfqModal() {
        this.setState({ showRfqModal: false });
    }

    async handleAddToQuote() {
        const parameterList = this.props.projectUpdateParameters || [];
        const configurationJson = parameterList.reduce((acc, param) => {
            acc[param.name] = { value: param.value, units: param.units };
            return acc;
        }, {});

        const productName = this.props.activeProject?.id || 'Unknown Product';
        const productDisplayName = this.props.activeProject?.label || productName;

        this.setState({ addingToQuote: true, addToQuoteError: '', addToQuoteSuccess: false });

        try {
            await quoteCartService.addItem({
                productName,
                productDisplayName,
                configurationJson: JSON.stringify(configurationJson),
                quantity: 1
            });
            if (this._isMounted) {
                this.setState({ addToQuoteSuccess: true, addingToQuote: false });
            }

            // Notify parent to refresh cart count
            if (this.props.onCartUpdated) {
                this.props.onCartUpdated();
            }

            // Reset success message after 3 seconds
            setTimeout(() => {
                if (this._isMounted) {
                    this.setState({ addToQuoteSuccess: false });
                }
            }, 3000);
        } catch (err) {
            if (this._isMounted) {
                this.setState({
                    addToQuoteError: err.message || 'Failed to add to quote',
                    addingToQuote: false
                });
            }
            setTimeout(() => {
                if (this._isMounted) {
                    this.setState({ addToQuoteError: '' });
                }
            }, 5000);
        }
    }

    render() {
        const parameterList = this.props.activeProject ? this.props.projectUpdateParameters : [];
        const buttonsContainerClass = parameterList ? "buttonsContainer" : "buttonsContainer hidden";

        const adoptWarning = this.props.adoptWarning;
        const tooltipProps = adoptWarning ? { openOnHover: true, content: () => <div className="warningButtonTooltip">{ adoptWarning }</div>  } : { open: false };
        const buttonProps = adoptWarning ? { type:"secondary", icon: <Alert24 style={ { color: "orange" }} /> } : { type: "primary" };

        const configurationJson = parameterList ? parameterList.reduce((acc, param) => {
            acc[param.name] = param.value;
            return acc;
        }, {}) : {};

        const productName = this.props.activeProject && this.props.activeProject.id ? this.props.activeProject.id : 'Unknown Product';

        return (
            <div className="parametersContainer">
                <div className="pencilContainer">
                </div>
                <div className="parameters">
                {
                    parameterList ?
                        parameterList.map((parameter, index) => (<Parameter key={index} parameter={parameter}/>))
                        : "No parameters"
                }
                </div>
                <hr className="parametersSeparator"/>
                <div className={buttonsContainerClass}>
                    <div style={{width: '100%'}}>
                        <Tooltip { ...tooltipProps } className="paramTooltip" anchorPoint="top-center">
                            <Button id="updateButton"
                                style={{width: '100%'}}
                                { ...buttonProps }
                                size="standard"
                                title="Configure My Options"
                                width="grow"
                                onClick={() => this.updateClicked()}/>
                        </Tooltip>
                    </div>
                    <div style={{height: '10px'}}/>
                    <Button style={{width: '100%'}}
                        size="standard"
                        title="Reset"
                        type="secondary"
                        width="grow"
                        onClick={() => {this.props.resetParameters(this.props.activeProject.id, this.props.projectSourceParameters);}}
                    />

                    {this.props.modalProgressShowing &&
                        <ModalProgress
                            open={this.props.modalProgressShowing}
                            title="Please be patient while your options are being applied."
                            doneTitle="Your Options Have Been Applied"
                            label={this.props.activeProject.id}
                            onClose={() => this.onModalProgressClose()}
                            warningMsg={this.props.adoptWarning}
                        />
                    }
                    {this.props.updateFailedShowing &&
                        <ModalFail
                            open={this.props.updateFailedShowing}
                            title="Update Failed"
                            contentName="Project:"
                            label={this.props.activeProject.id}
                            onClose={() => this.onUpdateFailedCloseClick()}
                            errorData={this.props.errorData}/>
                    }
                </div>
                
            </div>
        );
    }
}

/* istanbul ignore next */
export default connect(function (store) {
    const activeProject = getActiveProject(store);
    const adoptWarning = fullWarningMsg(activeProject.adoptWarnings);

    return {
        activeProject: activeProject,
        modalProgressShowing: modalProgressShowing(store),
        updateFailedShowing: updateFailedShowing(store),
        errorData: errorData(store),
        projectSourceParameters: getParameters(activeProject.id, store),
        projectUpdateParameters: getUpdateParameters(activeProject.id, store),
        adoptWarning: adoptWarning
    };
}, { fetchParameters, resetParameters, updateModelWithParameters, showModalProgress, showUpdateFailed, invalidateDrawing,
    hideModalProgress: () => async (dispatch) => { dispatch(showModalProgress(false)); } })(ParametersContainer);